import React, {forwardRef} from 'react';

import {SvgIcon, Switch, TextField, useTheme} from '@mui/material';
import {styled} from '@mui/system';
import {ReactMultiEmail} from 'react-multi-email';

import {ReactComponent as ChevronDown} from 'assets/svg/chevron-down-thin.svg';
import zoomPalette from 'zoomPalette';

export const ForwardRefTextField = forwardRef(
  ({children, ...props}, ref) => {
    return (<TextField
      {...props}
      InputLabelProps={{
        ...props?.InputLabelProps,
        shrink: true,
      }}
      InputProps={{
        ...props?.InputProps,
        ...((!props.variant || props?.variant === 'outlined') && {notched: false}),
      }}
      inputProps={{
        ...props?.inputProps,
      }}
      SelectProps={{
        ...((props?.select) && {
          IconComponent: (iconProps) =>
            <SvgIcon fontSize="inherit" component={ChevronDown} inheritViewBox {...iconProps} />,
        }),
        ...props?.SelectProps,
      }}
      ref={ref}
    >
      {children}
    </TextField>);
  },
);

const ForwardRefSwitch = forwardRef(
  ({children, ...props}, ref) => {
    return (<Switch
      {...props}
      inputProps={{
        ...props?.inputProps,
      }}
      ref={ref}
    >
      {children}
    </Switch>);
  },
);

const zmFocusStyle = ({theme}) => `
&:focus, &.Mui-focused {
  box-shadow: 0px 0px 0px 2px #FFFFFF, 0px 0px 0px 4px ${theme.palette.button.default};
  
  .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.palette.common.swatch[60]};
    border-width: 1px;
  }
}
`;

export const zmTextFieldBaseStyle = ({multiline, theme}) => `
root {
  color: ${theme.palette.text.primary};
  padding: 8px;
}
label {
  color: ${theme.palette.text.primary} !important;
  font-size: .875rem;
  font-weight: 500;
  line-height: 1.25rem;
  margin-bottom: 4px;
  transform: unset;
  position: initial;
  text-wrap: wrap;
}

& .MuiInputBase-root {
  font-size: .875rem;
  padding-top: 6px;
  padding-bottom: 6px;
  ${zmFocusStyle({theme})}
  ${!multiline && 'height: 2.5rem;'}

  & .MuiSelect-icon.MuiSelect-iconOutlined {
    right: 16px;
  }
}

& .MuiInputBase-sizeSmall {
  font-size: .875rem;
  ${!multiline && 'height: 2rem;'}
  .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input {
    padding: 6px 8px;
    display: flex;
    align-items: center;
  }
  .MuiInputBase-input.MuiOutlinedInput-input {
    padding: 6px 8px;
  }

  & .MuiSelect-icon.MuiSelect-iconOutlined {
    right: 8px;
  }
}

& .MuiOutlinedInput-input {
  padding-top: 8px;
  padding-bottom: 9px;
}

.MuiFormHelperText-root.Mui-error {
  color: ${theme.palette.error.swatch[70]};
}

& .MuiOutlinedInput-root.Mui-error {
  .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.palette.error.swatch[70]};
    border-width: 1px;
  }
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.palette.error.swatch[70]};
    border-width: 1px;
  }
}

& .MuiOutlinedInput-root {
  .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.palette.common.swatch[50]};
    border-width: 1px;
  }
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.palette.common.swatch[60]};
    border-width: 1px;
  }
}
& .MuiFilledInput-root {
  border-radius: 10px;
}
& .MuiFilledInput-root:before {
  border-bottom: unset;
  position: unset;
  content: unset;
  left: unset;
  right: unset;
  bottom: unset;
  transition: unset;
}
& .MuiFilledInput-root:after {
  border-bottom: unset;
  position: unset;
  content: unset;
  left: unset;
  right: unset;
  bottom: unset;
  transition: unset;
}
& .MuiFilledInput-input {
  padding-top: 10px;
  padding-bottom: 10px;
}
`;

const zmSwitchStyles = ({theme}) => `
padding: 0;
border-radius: 100px;
&:focus-within {
  box-shadow: 0px 0px 0px 2px #FFFFFF, 0px 0px 0px 4px #0E72ED;
}
& .MuiSwitch-switchBase {
  padding: 0;
  width: 100%;
  height: 100%;
  &.MuiButtonBase-root + .MuiSwitch-track {
    opacity: 1;
  }
}
& .MuiSwitch-thumb {
  position: absolute;
  box-shadow: 0px 1px 1px rgba(19, 22, 25, 0.4), 0px 1px 2px rgba(19, 22, 25, 0.2);
}
&.MuiSwitch-sizeMedium {
  width: 58px;
  height: 32px;
  .MuiSwitch-thumb {
    top: 3px;
    width: 26px;
    height: 26px;
    left: 3px;
  }
  .Mui-checked {
    transform: translateX(26px);
  }
}
&.MuiSwitch-sizeSmall {
  width: 36px;
  height: 20px;
  .MuiSwitch-thumb {
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
  }
  .Mui-checked {
    transform: translateX(16px);
  }
}
& .MuiSwitch-thumb {
  background: ${theme.palette.common.white};
}

& .MuiButtonBase-root + .MuiSwitch-track {
  background-color: ${theme.palette.common.swatch['60']}
}
&:hover .MuiButtonBase-root + .MuiSwitch-track {
  background-color: ${theme.palette.common.swatch['70']}
}
&:active .MuiButtonBase-root + .MuiSwitch-track {
  background-color: ${theme.palette.common.swatch['80']}
}
&:active .MuiButtonBase-root.Mui-disabled + .MuiSwitch-track {
  background-color: ${theme.palette.common.swatch['30']}
}

& .MuiButtonBase-root.Mui-checked.MuiSwitch-colorPrimary + .MuiSwitch-track {
  background: ${theme.palette.primary.swatch['60']};
}
&:hover .MuiButtonBase-root.Mui-checked.MuiSwitch-colorPrimary + .MuiSwitch-track {
  background: ${theme.palette.primary.swatch['70']};
}
&:active .MuiButtonBase-root.Mui-checked.MuiSwitch-colorPrimary + .MuiSwitch-track {
  background: ${theme.palette.primary.swatch['80']};
}
& .Mui-disabled.MuiButtonBase-root.Mui-checked.MuiSwitch-colorPrimary + .MuiSwitch-track {
  background: ${theme.palette.primary.swatch['20']};
}
& .MuiButtonBase-root.Mui-disabled + .MuiSwitch-track {
  background-color: ${zoomPalette.gray['30']}
}
`;

/**
 *
 * @param {TextFieldProps} props
 * @return {JSX.Element}
 */
export const ZmTextField = styled(ForwardRefTextField, {shouldForwardProp: () => true})(
  (props) =>
    `
    ${zmTextFieldBaseStyle(props)}
    `);

/**
 * Numeric text field with hidden up/down arrows
 * @param {ZmTextField}
 * @return {JSX.Element}
 */
export const ZmNumericTextField = styled(ZmTextField)`
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    display: none;
  }
`;

/**
 *
 * @param {SwitchProps} props
 * @return {JSX.Element}
 */
export const ZmSwitch = styled(ForwardRefSwitch, {shouldForwardProp: () => true})(
  (props) =>
    `
    ${zmTextFieldBaseStyle(props)}
    ${zmSwitchStyles(props)}
    `);

// TODO: the ReactMultiEmail package doesn't seem to give us much control for customizations.
// When we need more customization on this component in the future and they still don't have it,
// we may consider using MUI Autocomplete with multiple values and tags/chips enabled and disable/hide the search.
const CustomReactMultiEmail = styled(ReactMultiEmail)(({theme}) => `
  input {
    background: ${theme.palette.background.default} !important;
    color: ${theme.palette.text.primary} !important;
    font-size: .875rem;
  }
`);

/**
 *
 * @param {ReactMultiEmailProps} props
 * @return {JSX.Element}
 */
export const ZmMultiEmail = forwardRef(
  ({children, ...props}, ref) => {
    const theme = useTheme();
    const labelStyle = props?.labelStyle ?? {};
    return (<div>
      <label
        for={props?.id}
        style={{
          color: theme.palette.text.primary,
          fontSize: '.875rem',
          fontWeight: 500,
          lineHeight: '1.25rem',
          marginBottom: '4px',
          transform: 'unset',
          position: 'initial',
          ...labelStyle,
        }}
      >
        {props?.label}
      </label>
      <CustomReactMultiEmail
        id={props?.id}
        style={{
          color: zoomPalette.gray['90'],
          fontSize: '.875rem',
          paddingTop: '6px',
          paddingBottom: '6px',
          boxShadow: props?.focused ? '0px 0px 0px 2px #FFFFFF, 0px 0px 0px 4px #0E72ED' : null,
          borderColor: props?.error ? zoomPalette.red[70] : zoomPalette.gray['50'],
          borderWidth: '1px',
          borderRadius: '10px',
          background: theme.palette.background.default,
        }}
        {...props}
        ref={ref}
        getLabel={(email, index, removeEmail) => {
          return (
            <div data-tag key={index}>
              <div data-tag-item>{email}</div>
              <span data-tag-handle onClick={() => removeEmail(index)}>
                  ×
              </span>
            </div>
          );
        }}
        onChange={(_emails) => {
          props?.setEmails(_emails);
        }}
      >
        {children}
      </CustomReactMultiEmail>
      {props?.error &&
        <label
          style={{
            color: zoomPalette.red[70],
            fontSize: '.75rem',
            margin: '3px 14px 0px',
          }}
        >
          {props?.helperText}
        </label>
      }
    </div>);
  },
);
